'use client'

import React from 'react'
import '@mantine/carousel/styles.css'
import CallToActionCard, { CTAType } from '@/components/CallToActionCard'
import { InsuranceShieldIcon } from '@/components/Icons/InsuranceShieldIcon'
import { ServiceItemCardType } from '@/types/enums/ServiceItem'
import CareTeamHeadshots from '@/assets/images/care-team-headshots-2.png'
import { MessagesTextCircleIcon } from '@/components/Icons/MessagesTextCircleIcon'
import { useContactSupport } from '@/hooks/useContactSupport'
import ThickDivider from '@/components/shared/ThickDivider'
import { useIsMobile } from '@/lib/mobileDetector'
import { Box } from '@mantine/core'
import { setInsuranceReturnPath } from '@/lib/redirectFromInsuranceFlow'
import { usePathname, useRouter } from 'next/navigation'
import { VisitOptionVideoVisitIcon } from '../Icons/VisitOptionVideoVisitIcon'
import StoreTestimonials from '@/components/Store/StoreTestimonials'
import { useUserContext } from '@/contexts/UserContext'
import { PageLocation, StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'
import { useBookVisitDetailHref } from '@/hooks/useBookVisitHref'

function StoreWidget({
    cardType,
    pageLocation,
    additionalInformation,
}: {
    cardType: ServiceItemCardType
    pageLocation: PageLocation
    additionalInformation?: Record<string, any>
}) {
    const BASE_BACKGROUND_COLOR = 'rgba(43, 42, 42, 0.05)'
    const isMobile = useIsMobile()
    const router = useRouter()
    const currentPath = usePathname()
    const { user } = useUserContext()
    const bookVisitDetailHref = useBookVisitDetailHref()
    const { contactUsLabel, contactUsAction } = useContactSupport({
        defaultLabel: 'Text us',
        defaultAction: (openSms) => (user ? openSms() : router.push('/get-started')),
        onAction: (destination) =>
            trackStoreEvent(StoreAnalytics.GUIDANCE_CLICKED_TEXT_US, {
                location: pageLocation,
                additionalInformation,
                destination: destination === 'message-center' ? 'message-center' : user ? 'sms' : 'get-started',
            }),
    })
    const { contactUsLabel: supportSubtitle } = useContactSupport({
        defaultLabel: 'Have questions or need another service? Licensed clinicians are just a text away.',
    })
    const addInsuranceClick = () => {
        setInsuranceReturnPath(currentPath)
        router.push(`/insurance`)
        trackStoreEvent(StoreAnalytics.CLICKED_ADD_INSURANCE, {
            location: pageLocation,
            additionalInformation,
        })
    }

    const handleSupportCTAPrimaryClick = () => {
        router.push(bookVisitDetailHref)
        trackStoreEvent(StoreAnalytics.GUIDANCE_CLICKED_BOOK_VISIT, {
            location: pageLocation,
            additionalInformation,
        })
    }

    if (cardType === ServiceItemCardType.insurance) {
        return renderInsuranceCard()
    } else if (cardType === ServiceItemCardType.support) {
        return renderSupportCard()
    } else if (cardType === ServiceItemCardType.testimonial) {
        return <StoreTestimonials />
    } else if (cardType === ServiceItemCardType.divider && isMobile) {
        return <ThickDivider />
    }

    function renderInsuranceCard() {
        return (
            <CallToActionCard
                title={"See what you'll pay with insurance"}
                subtitle={'Securely add your insurance info in seconds to see exact prices.'}
                actionLabel={'Add my insurance'}
                actionIcon={<InsuranceShieldIcon />}
                actionHref="/insurance"
                onClick={addInsuranceClick}
                align="left"
                ctaType={CTAType.GAP}
            />
        )
    }

    function renderSupportCard() {
        return (
            <Box
                p={isMobile ? 0 : 24}
                bg={isMobile ? '' : BASE_BACKGROUND_COLOR}
                style={{ borderRadius: 'var(--border-16, 16px)' }}
            >
                <CallToActionCard
                    image={{ src: CareTeamHeadshots.src, alt: 'Care team headshots' }}
                    header={'Our clinicians are here to help'}
                    subtitle={supportSubtitle}
                    secondaryActionIcon={<MessagesTextCircleIcon color="var(--gm-gray-9)" />}
                    secondaryActionLabel={contactUsLabel}
                    secondaryActionHref={user ? undefined : '/get-started'}
                    actionLabel={'Book a visit'}
                    actionIcon={<VisitOptionVideoVisitIcon size={20} color="white" strokeWidth="1.5" />}
                    actionHref={bookVisitDetailHref}
                    secondaryOnClick={contactUsAction}
                    onClick={handleSupportCTAPrimaryClick}
                    actionSubtitle={'Mon-Fri 9am – 9pm EDT'}
                    align={'center'}
                    transparent
                />
            </Box>
        )
    }
}

export default StoreWidget
